import { useMemo, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { fmtBRL } from '../../db/api.js'

const SOURCE_COLORS = {
  google_ads: '#4285f4',
  meta_ads: '#1877f2',
  organico: '#34a853',
  manual: '#9e9e9e',
}

const SOURCE_LABELS = {
  google_ads: 'Google',
  meta_ads: 'Meta',
  organico: 'Organico',
  manual: 'Manual',
}

const PIPELINE_COLS = [
  { id: 'visitantes', title: 'Visitantes Site', icon: 'eye', accent: '#6e6e73' },
  { id: 'formularios', title: 'Formularios', icon: 'page', accent: '#0071e3' },
  { id: 'whatsapp', title: 'Click WhatsApp', icon: 'whats', accent: '#34a853' },
  { id: 'leads', title: 'Leads', icon: 'users', accent: '#a259ff' },
  { id: 'qualificado', title: 'Lead Qualificado', icon: 'search', accent: '#ff9f0a' },
  { id: 'venda', title: 'Venda', icon: 'bank', accent: '#e85d04' },
]

const STAGE_IDS = new Set(PIPELINE_COLS.map((column) => column.id))

function OutcomeBadge({ outcome }) {
  if (!outcome) return null
  if (outcome === 'ganho') {
    return <div className="kbc-outcome-badge won">✓ Ganho</div>
  }
  if (outcome === 'perdido') {
    return <div className="kbc-outcome-badge lost">✗ Perdido</div>
  }
  return null
}

export default function LeadsKanban({ leads, onLeadClick, onStatusChange }) {
  const [dropTarget, setDropTarget] = useState(null)

  const groupedLeads = useMemo(() => {
    const grouped = {}
    PIPELINE_COLS.forEach((column) => {
      grouped[column.id] = []
    })
    leads.forEach((lead) => {
      const rawCol = lead.stage || lead.status
      const columnId = STAGE_IDS.has(rawCol) ? rawCol : 'visitantes'
      grouped[columnId].push(lead)
    })
    return grouped
  }, [leads])

  const colStats = useMemo(() => {
    const stats = {}
    PIPELINE_COLS.forEach(({ id }) => {
      const all = groupedLeads[id] || []
      const active = all.filter((lead) => !lead.outcome)
      const won = all.filter((lead) => lead.outcome === 'ganho')
      const lost = all.filter((lead) => lead.outcome === 'perdido')
      stats[id] = {
        total: all.length,
        active: active.length,
        won: won.length,
        lost: lost.length,
        pipelineValue: active.reduce((sum, lead) => sum + (lead.estimated_value || 0), 0),
      }
    })
    return stats
  }, [groupedLeads])

  const handleDragStart = (event, id) => {
    event.dataTransfer.setData('leadId', id)
    event.currentTarget.classList.add('is-dragging')
  }

  const handleDragEnd = (event) => {
    setDropTarget(null)
    event.currentTarget.classList.remove('is-dragging')
  }

  const handleDrop = (event, nextStage) => {
    event.preventDefault()
    const id = event.dataTransfer.getData('leadId')
    setDropTarget(null)
    if (!id || !onStatusChange) return
    onStatusChange(id, { stage: nextStage })
  }

  return (
    <div className="kanban-wrapper">
      <div className="kbc-legend">
        <div className="kbc-legend-item">
          <div className="kbc-legend-dot" style={{ background: 'var(--border)' }} />
          <span>Ativo no pipeline</span>
        </div>
        <div className="kbc-legend-item">
          <div className="kbc-legend-dot" style={{ background: '#30d158' }} />
          <span>Ganho ✓</span>
        </div>
        <div className="kbc-legend-item">
          <div className="kbc-legend-dot" style={{ background: '#ff3b30' }} />
          <span>Perdido</span>
        </div>
      </div>

      <div className="kanban-scroll-area">
        <div
          className="kanban-board-compact"
          style={{ gridTemplateColumns: `repeat(${PIPELINE_COLS.length}, minmax(0, 1fr))` }}
        >
          {PIPELINE_COLS.map((column) => {
            const stats = colStats[column.id]
            return (
              <div
                key={column.id}
                className={`kbc-col ${dropTarget === column.id ? 'drop-active' : ''}`}
                onDragOver={(event) => {
                  event.preventDefault()
                  setDropTarget(column.id)
                }}
                onDragLeave={() => setDropTarget(null)}
                onDrop={(event) => handleDrop(event, column.id)}
                style={{
                  transition: 'all 0.2s ease',
                  backgroundColor: dropTarget === column.id ? `${column.accent}11` : 'transparent',
                  border: dropTarget === column.id ? `2px dashed ${column.accent}66` : '2px solid transparent',
                  borderRadius: '12px',
                }}
              >
                <div className="kbc-col-head" style={{ borderTop: `3px solid ${column.accent}` }}>
                  <div className="kbc-col-title">
                    <Icon n={column.icon} s={13} c={column.accent} />
                    <span>{column.title}</span>
                    <div className="kbc-counters">
                      <span className="kbc-cnt-active" style={{ background: column.accent }}>
                        {stats.active}
                      </span>
                      {stats.won > 0 ? <span className="kbc-cnt-won">{stats.won}✓</span> : null}
                      {stats.lost > 0 ? <span className="kbc-cnt-lost">{stats.lost}✗</span> : null}
                    </div>
                  </div>
                  {stats.pipelineValue > 0 ? (
                    <div className="kbc-col-value">{fmtBRL(stats.pipelineValue)}</div>
                  ) : null}
                </div>

                <div className="kbc-cards">
                  {groupedLeads[column.id].map((lead) => {
                    const srcColor = SOURCE_COLORS[lead.source] || '#9e9e9e'
                    const srcLabel = SOURCE_LABELS[lead.source] || 'Manual'
                    const isHot = !lead.outcome && (lead.estimated_value || 0) > 5000
                    const daysIdle = Math.floor(
                      (Date.now() - new Date(lead.last_interaction || lead.created_at)) / 86400000
                    )
                    const isCold = !lead.outcome && daysIdle > 5
                    const isWon = lead.outcome === 'ganho'
                    const isLost = lead.outcome === 'perdido'

                    return (
                      <div
                        key={lead.id}
                        className={[
                          'kbc-card',
                          isHot ? 'card-hot' : '',
                          isCold ? 'card-cold' : '',
                          isWon ? 'card-outcome-won' : '',
                          isLost ? 'card-outcome-lost' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        draggable={!lead.outcome}
                        onDragStart={(event) => !lead.outcome && handleDragStart(event, lead.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onLeadClick(lead)}
                      >
                        <OutcomeBadge outcome={lead.outcome} />

                        <div className="kbc-name">
                          {lead.name === 'Lead sem nome'
                            ? lead.phone !== 'N/A'
                              ? lead.phone
                              : lead.id_user_whats || 'Lead novo'
                            : lead.name}
                          {lead.is_qualified && !lead.outcome ? (
                            <span className="qual-dot" title="Qualificado">
                              ●
                            </span>
                          ) : null}
                          {isHot ? <span className="hot-badge">HOT</span> : null}
                        </div>

                        {lead.location && lead.location !== '—' ? (
                          <div className="kbc-company" style={{ color: 'var(--sec)', fontSize: 10 }}>
                            <Icon n="geo" s={10} /> {lead.location}
                          </div>
                        ) : null}

                        <div className="kbc-origin-row">
                          <div className="kbc-src-dot" style={{ background: srcColor }} />
                          <span className="kbc-src-label" style={{ color: srcColor }}>
                            {srcLabel}
                          </span>
                          {lead.keyword ? (
                            <span className="kbc-campaign" title={`Keyword: ${lead.keyword}`}>
                              {lead.keyword.length > 12 ? `${lead.keyword.slice(0, 12)}…` : lead.keyword}
                            </span>
                          ) : null}
                        </div>

                        <div className="kbc-footer">
                          <span
                            className="kbc-value"
                            style={{
                              color:
                                lead.estimated_value > 0
                                  ? isWon
                                    ? '#30d158'
                                    : 'var(--text)'
                                  : 'var(--ter)',
                            }}
                          >
                            {lead.estimated_value > 0 ? fmtBRL(lead.estimated_value) : '—'}
                          </span>
                          {isCold ? (
                            <span className="kbc-idle">
                              <Icon n="clock" s={10} />
                              {daysIdle}d
                            </span>
                          ) : (
                            <span className="kbc-date">
                              {(lead.created_at || '').slice(0, 10).split('-').reverse().join('/')}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}

                  {groupedLeads[column.id].length === 0 ? (
                    <div className="kbc-empty">Sem leads</div>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
